import { Array, identity, pipe } from 'effect'

import type { ReferenceTo } from '../../../../index.ts'
import type {
  NodePropertyName,
  ViewConfigField,
  ViewConfigFilterNode,
  ViewConfigSheet,
  ViewConfigTreeNode,
  ViewConfigUiComponent,
} from '../../../../schemas/view_config/index.ts'
import { flatNodes } from '../../../query/executeViewQuery/util.ts'
import type { CompileContext } from '../../common.ts'

export function expandChildrenByReferenceField(
  tree: ViewConfigSheet['tree'],
  {
    fields,
    filterTree,
    components,
  }: {
    fields?: ViewConfigField[]
    // eslint-disable-next-line sonarjs/no-redundant-optional
    filterTree?: ViewConfigFilterNode | undefined
    components?: ViewConfigUiComponent[]
  },
  context: Pick<CompileContext, 'modelSearcher' | 'logs'>,
): ViewConfigTreeNode | undefined {
  if (tree === undefined) {
    return undefined
  }

  const nodeProperties = [
    ...(fields ?? []).map((x) => x.property),
    ...pipe(
      flatNodes(filterTree),
      Array.flatMap((x) => x.leafs),
      Array.flatMapNullishOr((x) => {
        if (x.type === 'property') {
          return x.property
        }
      }),
    ),
    ...pipe(
      components ?? [],
      Array.flatMap((x) => flatNodes(x)),
      Array.flatMapNullishOr((x) => x.property),
    ),
  ]
  return rec(tree, nodeProperties, context)
}

function rec(
  node: ViewConfigTreeNode,
  nodeProperties: NodePropertyName[],
  context: Pick<CompileContext, 'modelSearcher' | 'logs'>,
): ViewConfigTreeNode | undefined {
  const model = context.modelSearcher.searchModel(node.modelName)
  if (model === undefined) {
    context.logs.warn(
      `[expandChildrenByReferenceField] model not found. ${node.modelName}, current: ${context.modelSearcher.models
        .map((x) => x.name)
        .join(',')}`,
    )
    return undefined
  }

  const children = Array.flatMapNullishOr(
    (node.children ?? []).map((child) => rec(child, nodeProperties, context)),
    identity,
  )
  const additionalChildren = getAdditionalChildren(node, nodeProperties, context) // childrenに含まれるnodeは、getAdditionalChildren側で弾かれる

  return {
    ...node,
    children: [...children, ...additionalChildren],
  }
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO: polymorphic な ID に対して、 SALESCORE 側でちゃんと対応する。
const polymorphicModelToSingleModel = new Map<string, string>([
  ['owner_id', 'salesforce_user'],
  ['what_id', 'salesforce_account'],
])

// eslint-disable-next-line sonarjs/todo-tag
// TODO: polymorphic な ID に対して、 SALESCORE 側でちゃんと対応する。現状は下記のように一つのオブジェクトに決め打ちしている。
export function getReferenceByHack(
  modelName: string,
  propertyName: string,
  references: ReferenceTo[] | undefined,
): ReferenceTo | undefined {
  const mappedModelName = polymorphicModelToSingleModel.get(propertyName)
  if (!modelName.startsWith('salesforce_') || mappedModelName === undefined) {
    return references?.at(0)
  }
  return references?.find((x) => x.modelName === mappedModelName)
}

function getAdditionalChildren(
  parentConfigNode: ViewConfigTreeNode,
  nodeProperties: NodePropertyName[],
  context: Pick<CompileContext, 'modelSearcher' | 'logs'>,
): ViewConfigTreeNode[] {
  const properties = pipe(
    nodeProperties,
    Array.filter((nodeProperty) => nodeProperty.nodeName === parentConfigNode.name),
    Array.flatMapNullishOr((nodeProperty) => {
      const property = context.modelSearcher.searchProperty(nodeProperty.modelName, nodeProperty.propertyName)
      if (property === undefined) {
        return
      }
      return {
        property,
        nodeProperty,
      }
    }),
    Array.dedupeWith((a, b) => a.property.name === b.property.name),
  )

  return pipe(
    properties,
    Array.flatMapNullishOr(({ property, nodeProperty }): ViewConfigTreeNode | undefined => {
      if (property.referenceTo === undefined) {
        return undefined
      }
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: 参照項目について、polymorphicに対応できていない
      const reference = getReferenceByHack(nodeProperty.modelName, nodeProperty.propertyName, property.referenceTo)
      if (reference === undefined) {
        return
      }

      // config側で既に追加済みであればスキップする
      const isAlreadyJoined = (parentConfigNode.children ?? []).some((childConfigNode) => {
        const x = childConfigNode.referenceToProperty
        const isUsingSameProperty =
          x?.propertyName === nodeProperty.propertyName && x.modelName === nodeProperty.modelName
        const isReferenceSameModle = childConfigNode.modelName === reference.modelName
        return isUsingSameProperty && isReferenceSameModle
      })
      if (isAlreadyJoined) {
        return
      }

      const model = context.modelSearcher.searchModel(reference.modelName)
      if (model === undefined) {
        return
      }
      // eslint-disable-next-line sonarjs/todo-tag
      // const name = ['r', nodeProperty.nodeName, nodeProperty.propertyName, nodeProperty.modelName].join('_') // TODO: uniqueにはなるものの、長すぎ?
      // eslint-disable-next-line sonarjs/todo-tag
      const name = [nodeProperty.nodeName, nodeProperty.propertyName].join('_') // TODO: 上記のようにrを明示したかったが、元のロジックとの互換性をとるため

      return {
        type: 'model',
        name,
        modelName: reference.modelName,
        referenceToProperty: {
          nodeName: nodeProperty.nodeName,
          modelName: nodeProperty.modelName,
          propertyName: nodeProperty.propertyName,
          referenceTo: reference,
        },
      }
    }),
  )
}
